import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, FileText, RefreshCw, Plus, UserX, UserCheck, ChevronDown, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtData = (s) => {
  if (!s) return '—';
  const d = s.slice(0, 10).split('-');
  return `${d[2]}/${d[1]}/${d[0]}`;
};

const ESOCIAL_BADGE = {
  rascunho:      { label: 'Rascunho',      cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  assinado:      { label: 'Assinado',       cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  transmitido:   { label: 'Transmitido',    cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  processado_ok: { label: 'Aceito',         cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  rejeitado:     { label: 'Rejeitado',      cls: 'bg-red-50 text-red-700 border-red-300' },
};

const AfastamentoTab = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('todos');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtro !== 'todos' ? `?status=${filtro}` : '';
      const r = await axios.get(`${API}/api/calculos-dp/afastamentos${params}`, authHeader());
      setItems(r.data || []);
    } catch (e) {
      toast.error('Erro ao carregar afastamentos');
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);

  // Agrupar por funcionário
  const grupos = useMemo(() => {
    const map = {};
    for (const item of items) {
      const key = item.employee_id || 'unknown';
      if (!map[key]) {
        map[key] = {
          employee_id: item.employee_id,
          employee_nome: item.employee_nome || '—',
          employee_cargo: item.employee_cargo || '—',
          afastamentos: [],
        };
      }
      map[key].afastamentos.push(item);
    }
    return Object.values(map).sort((a, b) => a.employee_nome.localeCompare(b.employee_nome));
  }, [items]);

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const totalAtivos = items.filter((i) => i.status === 'ativo').length;
  const totalRetornados = items.filter((i) => i.status === 'retornado').length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-500" />
            Relatório de Afastamentos
          </CardTitle>
          <CardDescription>
            Histórico de afastamentos registrados via eSocial (S-2230).
            {totalAtivos > 0 && <span> <strong>{totalAtivos}</strong> ativo{totalAtivos > 1 ? 's' : ''} · </span>}
            {totalRetornados > 0 && <span><strong>{totalRetornados}</strong> retornado{totalRetornados > 1 ? 's' : ''}</span>}
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="flex items-center justify-between">
        <Tabs value={filtro} onValueChange={setFiltro}>
          <TabsList>
            <TabsTrigger value="todos">Todos {items.length > 0 && `(${items.length})`}</TabsTrigger>
            <TabsTrigger value="ativo" className="flex items-center gap-1">
              <UserX className="h-3.5 w-3.5" />Em Afastamento
            </TabsTrigger>
            <TabsTrigger value="retornado" className="flex items-center gap-1">
              <UserCheck className="h-3.5 w-3.5" />Retornados
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate('/dp-folha/esocial', { state: { tab: 'gerar', tipo: 'S-2230' } })}>
            <Plus className="h-4 w-4 mr-1" />
            Novo Afastamento
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : grupos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>Nenhum afastamento encontrado</p>
            <p className="text-sm mt-1">Crie um evento <strong>S-2230 (Afastamento)</strong> na tela eSocial</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {grupos.map((grupo) => {
            const isOpen = expanded[grupo.employee_id] !== false;
            return (
              <Card key={grupo.employee_id} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleExpand(grupo.employee_id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <div>
                      <span className="font-medium text-slate-800">{grupo.employee_nome}</span>
                      {grupo.employee_cargo !== '—' && (
                        <span className="text-sm text-slate-500 ml-2">{grupo.employee_cargo}</span>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {grupo.afastamentos.length} afastamento{grupo.afastamentos.length > 1 ? 's' : ''}
                  </Badge>
                </button>
                {isOpen && (
                  <div className="overflow-x-auto border-t border-slate-100">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="text-left p-2.5 pl-4 font-medium">Início</th>
                          <th className="text-left p-2.5 font-medium">Fim</th>
                          <th className="text-left p-2.5 font-medium">Dias</th>
                          <th className="text-left p-2.5 font-medium">Motivo</th>
                          <th className="text-center p-2.5 font-medium">Situação eSocial</th>
                          <th className="text-center p-2.5 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {grupo.afastamentos.map((item) => {
                          const esocialCfg = ESOCIAL_BADGE[item.esocial_status] || { label: item.esocial_status || '—', cls: 'bg-slate-100 text-slate-600' };
                          return (
                            <tr key={item.afastamento_id} className="hover:bg-slate-50 transition-colors">
                              <td className="p-2.5 pl-4 whitespace-nowrap">{fmtData(item.data_inicio)}</td>
                              <td className="p-2.5 whitespace-nowrap">{fmtData(item.data_fim)}</td>
                              <td className="p-2.5">
                                {item.dias_afastamento != null ? `${item.dias_afastamento}d` : '—'}
                              </td>
                              <td className="p-2.5 text-slate-500 max-w-[220px] truncate" title={item.motivo || ''}>
                                {item.motivo || '—'}
                              </td>
                              <td className="p-2.5 text-center">
                                <Badge variant="outline" className={`${esocialCfg.cls} border text-[11px]`}>
                                  {esocialCfg.label}
                                </Badge>
                              </td>
                              <td className="p-2.5 text-center">
                                {item.status === 'ativo' ? (
                                  <Badge variant="destructive" className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200">
                                    Em afastamento
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                    Retornado
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AfastamentoTab;
